/**
 * Bug Reporter — auto-captures runtime errors during enterprise lessons
 * and submits user-reported issues. Active only while a lesson is running.
 *
 * Auto path: window.onerror + unhandledrejection → POST /api/v1/bugs (type=auto)
 * Manual path: bugReporter.reportUser(text) → POST /api/v1/bugs (type=user_report)
 */
import { enterpriseStore } from '../stores/enterpriseStore';
import { uiStore } from '../stores/uiStore';
import { zoneStore } from '../stores/zoneStore';

interface AutoContext {
  layout?: string;
  activeZonePreset?: string;
  workspace?: string;
}

let active = false;
let activeLessonId: string | null = null;
const sessionSeen = new Set<string>(); // dedupe per page-load

let errorListener: ((e: ErrorEvent) => void) | null = null;
let rejectionListener: ((e: PromiseRejectionEvent) => void) | null = null;

function gatherContext(): AutoContext {
  const ctx: AutoContext = {};
  try {
    const ui = uiStore.getState();
    ctx.layout = ui.activeLayout;
    ctx.workspace = ui.workspace;
  } catch {}
  try {
    const z = zoneStore.getState();
    ctx.activeZonePreset = z.getActivePreset?.()?.name;
  } catch {}
  return ctx;
}

function currentStepId(): string | null {
  try {
    const ent = enterpriseStore.getState();
    return `s${ent.currentSectionIndex}.${ent.currentStepIndex}`;
  } catch {
    return null;
  }
}

function dedupeKey(message: string, stack: string | undefined): string {
  return `${message}::${(stack ?? '').slice(0, 200)}`;
}

async function post(payload: Record<string, unknown>): Promise<void> {
  try {
    await fetch('/api/v1/bugs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
  } catch {
    // Network failure — swallow; never let the reporter itself throw.
  }
}

function report(err: unknown, source: 'error' | 'unhandledrejection'): void {
  if (!active) return;
  let message = 'Unknown error';
  let stack: string | undefined;

  if (err instanceof Error) {
    message = err.message || err.name || 'Error';
    stack = err.stack;
  } else if (typeof err === 'string') {
    message = err;
  } else if (err && typeof err === 'object') {
    message = (err as any).message ?? JSON.stringify(err).slice(0, 500);
    stack = (err as any).stack;
  }

  const key = dedupeKey(message, stack);
  if (sessionSeen.has(key)) return;
  sessionSeen.add(key);

  post({
    type: 'auto',
    message,
    stack,
    lessonId: activeLessonId,
    stepId: currentStepId(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    context: { source, ...gatherContext() },
  });
}

export function startBugReporter(lessonId: string): void {
  if (active) return;
  active = true;
  activeLessonId = lessonId;
  sessionSeen.clear();

  errorListener = (e: ErrorEvent) => report(e.error ?? e.message, 'error');
  rejectionListener = (e: PromiseRejectionEvent) => report(e.reason, 'unhandledrejection');

  window.addEventListener('error', errorListener);
  window.addEventListener('unhandledrejection', rejectionListener);
}

export function stopBugReporter(): void {
  if (!active) return;
  active = false;
  activeLessonId = null;
  if (errorListener) window.removeEventListener('error', errorListener);
  if (rejectionListener) window.removeEventListener('unhandledrejection', rejectionListener);
  errorListener = null;
  rejectionListener = null;
}

/**
 * Submit a user-typed report. Works whether or not a lesson is active —
 * the breadcrumb-bar button calls this with the current lesson id.
 */
export function reportUserIssue(text: string, lessonId?: string | null): Promise<void> {
  return post({
    type: 'user_report',
    message: text.slice(0, 2000),
    lessonId: lessonId ?? activeLessonId,
    stepId: currentStepId(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    context: gatherContext(),
  });
}
